<template>
    <div class="blocked-dashboard">
        <VHeader page="Dashboard"></VHeader>
        <div class="blocked-dashboard__content">
            <div class="blocked-dashboard__apps">
                <div class="blocked-dashboard__section-title">
                    Your apps
                </div>
                <div
                    v-if="!apps.length"
                    class="blocked-dashboard__empty"
                >
                    No apps available.
                </div>
                <div
                    v-for="app in apps"
                    :key="app.bundle_id"
                    class="blocked-dashboard__app"
                >
                    <div
                        class="blocked-dashboard__app-icon"
                        :style="{backgroundImage: `url('${app.logo_small}')`}"
                    ></div>
                    <div class="blocked-dashboard__app-info">
                        <div class="blocked-dashboard__app-title">
                            {{ app.title }}
                        </div>
                        <div class="blocked-dashboard__app-bundle">
                            <span class="blocked-dashboard__bundle-id">{{ app.bundle_id }}</span>
                            <button
                                type="button"
                                class="blocked-dashboard__copy"
                                :class="{'blocked-dashboard__copy_copied': copiedValue === app.bundle_id}"
                                :title="copiedValue === app.bundle_id ? 'Copied!' : 'Copy bundle id'"
                                @click="copy(app.bundle_id)"
                            >
                                <span class="blocked-dashboard__copy-icon"></span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div class="blocked-dashboard__promo">
                <a
                    class="blocked-dashboard__promo-text"
                    href="https://claude.ai/"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    Шесть фильтров, четыре лоадера загрузки, три вкладки, сорок минут жизни — и в финале принимаем случайность за корреляцию. Или пара правильных промптов для Клода.
                </a>

                <div class="blocked-dashboard__setup">
                    <div class="blocked-dashboard__tips-title">
                        Подключить коннектор (один раз):
                    </div>
                    <ol class="blocked-dashboard__setup-list">
                        <li>
                            Открой Claude.ai → клик по аватарке → <strong>Customize → Connectors</strong>
                        </li>
                        <li>
                            Найди в списке <strong>«skarb»</strong> — коннектор уже добавлен в воркспейс Bitlica
                        </li>
                        <li>
                            Нажми <strong>Connect</strong> → пройди OAuth (войди своим скарбовым аккаунтом) → готово
                        </li>
                    </ol>

                    <BlockedGallery :slides="setupSlides" />

                    <a
                        class="blocked-dashboard__connectors-link"
                        href="https://claude.ai/customize/connectors"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Open Connectors
                    </a>
                </div>

                <div class="blocked-dashboard__tips">
                    <div class="blocked-dashboard__tips-title">
                        Как работаем с Клодом:
                    </div>
                    <ul class="blocked-dashboard__tips-list">
                        <li>
                            Промпт&nbsp;— это ТЗ, а не запрос в гугл. Прямо называйте, на какие данные и колонки смотреть. Обязательно уточняйте, что нужен когортный анализ&nbsp;— по дефолту Клод может уйти в некогортный, и весь вывод поедет не туда.
                        </li>
                        <li>
                            Заведите проект в Клоде и складывайте туда инструкции для повторяющихся задач. Один раз настроите&nbsp;— дальше работает само, а не объясняете контекст с нуля каждое утро.
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import VHeader from '@/components/VHeader.vue';
    import BlockedGallery from './BlockedGallery.vue';
    import {getProjectsWithData} from '@/api/apiServices';

    export default {
        name: 'BlockedDashboard',
        components: {
            VHeader,
            BlockedGallery,
        },
        data() {
            return {
                copiedValue: '',
                copyResetTimer: null,
                setupSlides: [
                    {
                        src: '/img/mcp-setup/1.png',
                        alt: 'Список коннекторов с подсвеченным skarb',
                        caption: 'Найди в списке «skarb» и нажми Connect',
                    },
                    {
                        src: '/img/mcp-setup/2.png',
                        alt: 'Коннектор skarb подключён, доступны 14 инструментов',
                        caption: 'Готово — коннектор подключён, доступны 14 инструментов',
                    },
                ],
            };
        },
        computed: {
            apps() {
                return getProjectsWithData();
            },
        },
        beforeDestroy() {
            if (this.copyResetTimer) {
                clearTimeout(this.copyResetTimer);
            }
        },
        methods: {
            copy(value) {
                const setCopied = () => {
                    this.copiedValue = value;
                    if (this.copyResetTimer) {
                        clearTimeout(this.copyResetTimer);
                    }
                    this.copyResetTimer = setTimeout(() => {
                        this.copiedValue = '';
                    }, 1500);
                };

                if (navigator.clipboard && navigator.clipboard.writeText) {
                    navigator.clipboard.writeText(value).then(setCopied, () => this.fallbackCopy(value, setCopied));
                } else {
                    this.fallbackCopy(value, setCopied);
                }
            },
            fallbackCopy(text, onDone) {
                const textarea = document.createElement('textarea');
                textarea.value = text;
                textarea.style.position = 'fixed';
                textarea.style.opacity = '0';
                document.body.appendChild(textarea);
                textarea.select();
                try {
                    document.execCommand('copy');
                    onDone();
                } catch (e) {
                    this.$toastr && this.$toastr.e && this.$toastr.e('Failed to copy');
                }
                document.body.removeChild(textarea);
            },
        },
    };
</script>

<style
    scoped
    lang="scss"
>
    @import "../../scss/base/colors";
    @import "../../scss/base/shadow";

    .blocked-dashboard {
        color: $dark-blue-color;

        &__content {
            display: grid;
            grid-template-columns: minmax(320px, 420px) 1fr;
            gap: 30px;
            padding: 26px 30px 40px 30px;
            align-items: start;
        }

        &__section-title {
            font-size: 16px;
            line-height: 20px;
            font-weight: 700;
            margin-bottom: 14px;
            text-align: left;
        }

        &__apps {
            @include dark-shadow;
            background: #FFFFFF;
            border-radius: 10px;
            padding: 18px 20px;
            max-height: calc(100vh - 140px);
            overflow-y: auto;
        }

        &__empty {
            color: #6F6F70;
            font-size: 13px;
        }

        &__app {
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 8px 0;
            border-bottom: 1px solid rgba(96, 137, 231, 0.15);

            &:last-child {
                border-bottom: none;
            }
        }

        &__app-icon {
            width: 36px;
            height: 36px;
            border-radius: 6px;
            background-size: cover;
            background-repeat: no-repeat;
            background-position: center;
            flex-shrink: 0;
            background-color: #F0F2FB;
        }

        &__app-info {
            display: flex;
            flex-direction: column;
            min-width: 0;
            flex: 1;
        }

        &__app-title {
            font-weight: 600;
            font-size: 14px;
            line-height: 18px;
            text-align: left;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        &__app-bundle {
            display: flex;
            align-items: center;
            gap: 6px;
            font-size: 12px;
            color: #3F3F40;
            margin-top: 2px;
        }

        &__bundle-id {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            min-width: 0;
        }

        &__copy {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 22px;
            height: 22px;
            background: transparent;
            border: none;
            border-radius: 4px;
            cursor: pointer;
            padding: 0;
            flex-shrink: 0;

            &:hover {
                background: rgba(96, 137, 231, 0.12);
            }

            &_copied .blocked-dashboard__copy-icon {
                background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14"><path fill="none" stroke="%2302DFB0" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" d="M2 7.5L5.5 11l7-8"/></svg>');
            }
        }

        &__copy-icon {
            width: 14px;
            height: 14px;
            background-repeat: no-repeat;
            background-position: center;
            background-size: contain;
            background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14"><rect x="3" y="3" width="8" height="9" rx="1.5" fill="none" stroke="%236089E7" stroke-width="1.3"/><path d="M5.5 3V2.5A1.5 1.5 0 0 1 7 1h3a1.5 1.5 0 0 1 1.5 1.5V9" fill="none" stroke="%236089E7" stroke-width="1.3" stroke-linecap="round"/></svg>');
        }

        &__promo {
            @include dark-shadow;
            background: #FFFFFF;
            border-radius: 10px;
            padding: 28px 32px;
            text-align: left;
            display: flex;
            flex-direction: column;
            gap: 22px;
        }

        &__promo-text {
            display: block;
            font-size: 22px;
            line-height: 32px;
            font-weight: 700;
            color: $primary-color;
            text-decoration: none;

            &:hover {
                text-decoration: underline;
            }
        }

        &__setup {
            border-top: 1px solid rgba(96, 137, 231, 0.15);
            padding-top: 22px;
        }

        &__setup-list {
            margin: 10px 0 0;
            padding-left: 18px;
            font-size: 13px;
            line-height: 20px;
            color: #3F3F40;

            li {
                margin-bottom: 12px;

                &:last-child {
                    margin-bottom: 0;
                }
            }
        }

        &__connectors-link {
            display: inline-flex;
            align-items: center;
            margin-top: 14px;
            padding: 8px 16px;
            font-size: 13px;
            font-weight: 600;
            color: $primary-color;
            border: 1px solid $primary-color;
            border-radius: 8px;
            text-decoration: none;

            &:hover {
                background: rgba(96, 137, 231, 0.08);
            }
        }

        &__tips-title {
            font-size: 14px;
            font-weight: 700;
            margin-bottom: 10px;
        }

        &__tips-list {
            margin: 0;
            padding-left: 18px;
            font-size: 13px;
            line-height: 20px;
            color: #3F3F40;

            li {
                margin-bottom: 10px;

                &:last-child {
                    margin-bottom: 0;
                }
            }
        }
    }
</style>
